import React, { useCallback, useMemo, useRef, useState } from "react";
import { Minus, Plus, Maximize2, Search, Users, Lock } from "lucide-react";

const number = (value, fallback = 0) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const normalize = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const FURNITURE_LABELS = {
  dj: "DJ",
  bar: "Bar",
  photocall: "Photocall",
  arch: "Arche",
  stage: "Scène",
  dancefloor: "Piste de danse",
  cake: "Gâteau",
  buffet: "Buffet",
};
const FURNITURE_COLORS = {
  dancefloor: "#d9cfe8",
  stage: "#cdbf9a",
  bar: "#b9a48a",
  buffet: "#c9b79c",
  dj: "#9aa5b8",
  photocall: "#e4c6b8",
  arch: "#c6d8c0",
  cake: "#f0d9d9",
};

// The server holds positions either in pixels (older plans, 50 px per metre) or in
// metres (after the editor saved them). The editor tells them apart by magnitude;
// the same rule is applied here so both draw in the same place.
const LEGACY_PIXELS = 50;
export const toMetres = (value) => { const v = number(value); return Math.abs(v) > 50 ? v / LEGACY_PIXELS : v; };
export const sizeToMetres = (value, fallback) => { const v = number(value, fallback); return v > 10 ? v / LEGACY_PIXELS : v; };

/** A table in plan pixels, whichever unit the server stored it in. */
export function normalizeTable(table, scale = 50) {
  const round = !["rect", "rectangle", "square"].includes(table.shape);
  const seats = Math.max(0, Math.round(number(table.seats, 0)));
  const width = round && seats ? seats * 0.18 : sizeToMetres(table.width, 1.4);
  const height = round && seats ? seats * 0.18 : sizeToMetres(table.height, width);
  return { x: toMetres(table.x) * scale, y: toMetres(table.y) * scale, width: Math.max(24, width * scale), height: Math.max(24, height * scale) };
}

/** A piece of furniture in plan pixels. */
export function normalizeFurniture(item, scale = 50) {
  return {
    x: toMetres(item.x) * scale,
    y: toMetres(item.y) * scale,
    width: Math.max(12, sizeToMetres(item.width, 1.2) * scale),
    height: Math.max(12, sizeToMetres(item.height, 0.8) * scale),
  };
}

const isRound = (table) => !["rect", "rectangle", "square"].includes(table.shape);

// Chairs are laid out from the table geometry only, so the preview never needs
// any editing data: round tables get a ring, rectangular ones two long sides.
function chairPositions(table, seats) {
  const count = clamp(Math.round(number(seats, 0)), 0, 24);
  if (!count) return [];
  const width = Math.max(24, number(table.width, 120));
  const height = Math.max(24, number(table.height, 80));
  if (isRound(table)) {
    const radius = Math.max(width, height) / 2 + 13;
    return Array.from({ length: count }, (_, index) => {
      const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
      return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
    });
  }
  const top = Math.ceil(count / 2);
  const bottom = count - top;
  const row = (n, y) => Array.from({ length: n }, (_, i) => ({ x: -width / 2 + (width * (i + 0.5)) / n, y }));
  return [...row(top, -height / 2 - 13), ...row(bottom, height / 2 + 13)];
}

function Opening({ opening, wall, scale }) {
  if (!wall) return null;
  const x1 = number(wall.x1) * scale;
  const y1 = number(wall.y1) * scale;
  const x2 = number(wall.x2) * scale;
  const y2 = number(wall.y2) * scale;
  const ratio = clamp(number(opening.position, number(opening.offset)), 0, 1);
  const x = x1 + (x2 - x1) * ratio;
  const y = y1 + (y2 - y1) * ratio;
  const width = Math.max(10, number(opening.width, 0.9) * scale);
  const horizontal = Math.abs(x2 - x1) >= Math.abs(y2 - y1);

  return (
    <line
      x1={horizontal ? x - width / 2 : x}
      y1={horizontal ? y : y - width / 2}
      x2={horizontal ? x + width / 2 : x}
      y2={horizontal ? y : y + width / 2}
      stroke={opening.type === "window" ? "#8db7d8" : "#fcfaf5"}
      strokeWidth={opening.type === "window" ? 7 : 10}
      strokeLinecap="square"
    />
  );
}

/**
 * Rectangle (in plan pixels) that holds everything drawn: tables with their chairs,
 * furniture and walls. The plan opens framed on it, not on the room's top-left corner,
 * so a phone shows the tables straight away.
 */
export function contentBounds({ tables = [], furniture = [], walls = [], scale = 50, room = {} }) {
  const xs = [];
  const ys = [];
  tables.forEach((t) => { const r = Math.max(number(t.width, 120), number(t.height, 80)) / 2 + 24; xs.push(number(t.x) - r, number(t.x) + r); ys.push(number(t.y) - r, number(t.y) + r); });
  furniture.forEach((f) => { const r = Math.max(number(f.width, 60), number(f.height, 40)) / 2; xs.push(number(f.x) - r, number(f.x) + r); ys.push(number(f.y) - r, number(f.y) + r); });
  walls.forEach((w) => { xs.push(number(w.x1) * scale, number(w.x2) * scale); ys.push(number(w.y1) * scale, number(w.y2) * scale); });
  if (!xs.length) { xs.push(0, Math.max(500, number(room.width, 20) * scale)); ys.push(0, Math.max(350, number(room.height, 15) * scale)); }
  const pad = 30;
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  return { minX, minY, width: Math.max(200, Math.max(...xs) + pad - minX), height: Math.max(150, Math.max(...ys) + pad - minY) };
}

function statusColor(seated, capacity) {
  if (!capacity) return "#9baa91";
  if (seated >= capacity) return "#7f9a78"; // complète
  if (seated === 0) return "#c9c2b4"; // vide
  return "#d8b45a"; // partielle
}

/**
 * Non-interactive-for-editing rendering of the couple's current plan. The only
 * interactions are viewing ones: zoom, pan, search and selecting a table to read
 * its details. Nothing here ever calls a write endpoint.
 */
export default function ReadOnlyTablePlan({ plan }) {
  const { room = {}, tables = [], walls = [], openings = [], furniture = [] } = plan || {};
  const canSeeNames = Boolean(plan?.can_see_guest_names);
  const scale = number(room.scale, 50);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const dragRef = useRef(null);
  const svgRef = useRef(null);

  const safeTables = useMemo(() => (Array.isArray(tables) ? tables : []).map((table, index) => {
    const seats = Math.max(0, Math.round(number(table.seats, 0)));
    const seatedGuests = Array.isArray(table.seated_guests) ? table.seated_guests : [];
    return {
      ...table,
      key: table.id || `table-${index}`,
      label: String(table.name || `Table ${index + 1}`),
      ...normalizeTable(table, scale),
      seats,
      seated: Math.max(0, Math.round(number(table.seated_count, seatedGuests.length))),
      seatedGuests,
    };
  }), [tables, scale]);

  const safeFurniture = useMemo(() => (Array.isArray(furniture) ? furniture : []).map((item, index) => ({
    ...item,
    key: item.id || `furniture-${index}`,
    ...normalizeFurniture(item, scale),
    label: item.name || FURNITURE_LABELS[item.type] || "Élément",
  })), [furniture, scale]);

  const matches = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) return null;
    return new Set(safeTables
      .filter((table) => normalize(table.label).includes(needle)
        || table.seatedGuests.some((guest) => normalize(guest.name).includes(needle)))
      .map((table) => table.key));
  }, [query, safeTables]);

  const bounds = useMemo(() => contentBounds({ tables: safeTables, furniture: safeFurniture, walls, scale, room }), [safeTables, safeFurniture, walls, scale, room]);
  const width = bounds.width;
  const height = bounds.height;
  const selected = safeTables.find((table) => table.key === selectedId) || null;
  const totalSeated = safeTables.reduce((sum, table) => sum + table.seated, 0);
  const totalSeats = safeTables.reduce((sum, table) => sum + table.seats, 0);

  const applyZoom = useCallback((factor) => setZoom((current) => clamp(current * factor, 0.5, 4)), []);
  const resetView = useCallback(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, []);

  const onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    dragRef.current = { x: event.clientX, y: event.clientY, origin: offset, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = width / zoom / Math.max(1, rect.width);
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
    setOffset({ x: drag.origin.x - dx * ratio, y: drag.origin.y - dy * ratio });
  };
  const onPointerUp = () => { dragRef.current = null; };
  const onWheel = (event) => {
    if (!(event.ctrlKey || event.metaKey)) return; // let the page scroll normally
    event.preventDefault();
    applyZoom(event.deltaY < 0 ? 1.12 : 1 / 1.12);
  };
  const selectTable = (key) => {
    if (dragRef.current?.moved) return;
    setSelectedId((current) => (current === key ? null : key));
  };

  if (!room.id && safeTables.length === 0 && walls.length === 0 && safeFurniture.length === 0) {
    return <p className="py-16 text-center text-sm italic text-charcoal-soft">Le plan de table n’a pas encore été créé par les mariés.</p>;
  }

  const viewWidth = width / zoom;
  const viewHeight = height / zoom;
  const viewBox = `${bounds.minX + offset.x + (width - viewWidth) / 2} ${bounds.minY + offset.y + (height - viewHeight) / 2} ${viewWidth} ${viewHeight}`;

  return (
    <section className="w-full rounded-3xl border border-[#e9e1d4] bg-white p-4 shadow-[0_18px_40px_-34px_rgba(83,64,52,0.5)] md:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sage">Consultation</p>
          <h1 className="font-serif text-3xl text-charcoal">Plan de table</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-charcoal-soft">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Version actuelle préparée par les mariés · lecture seule
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
          <span className="rounded-full border border-sage/25 bg-sage/10 px-3 py-1 text-sage">
            {safeTables.length} table{safeTables.length > 1 ? "s" : ""}
          </span>
          {totalSeats > 0 ? (
            <span className="rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-charcoal-soft">
              {totalSeated} / {totalSeats} places occupées
            </span>
          ) : null}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={canSeeNames ? "Rechercher une table ou un invité" : "Rechercher une table"}
            aria-label="Rechercher dans le plan"
            className="w-full rounded-full border border-stone-200 bg-white/80 py-2 pl-9 pr-3 text-sm text-charcoal outline-none focus:border-sage"
          />
        </label>
        <div className="flex items-center gap-1" role="group" aria-label="Zoom du plan">
          <button type="button" onClick={() => applyZoom(1 / 1.25)} aria-label="Dézoomer" className="rounded-full border border-stone-200 bg-white/80 p-2 text-charcoal hover:bg-white"><Minus className="h-4 w-4" /></button>
          <span className="w-12 text-center text-xs font-semibold text-charcoal-soft">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => applyZoom(1.25)} aria-label="Zoomer" className="rounded-full border border-stone-200 bg-white/80 p-2 text-charcoal hover:bg-white"><Plus className="h-4 w-4" /></button>
          <button type="button" onClick={resetView} aria-label="Recentrer le plan" className="rounded-full border border-stone-200 bg-white/80 p-2 text-charcoal hover:bg-white"><Maximize2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-[#faf8f2]">
          <svg
            ref={svgRef}
            aria-label="Plan de table en lecture seule"
            className="min-h-[420px] w-full cursor-grab touch-none select-none active:cursor-grabbing"
            viewBox={viewBox}
            role="img"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onWheel={onWheel}
          >
            <defs>
              <pattern id="collab-grid" width={scale} height={scale} patternUnits="userSpaceOnUse">
                <path d={`M ${scale} 0 L 0 0 0 ${scale}`} fill="none" stroke="#e8e2d6" strokeWidth="1" />
              </pattern>
            </defs>
            <rect x={bounds.minX - 4000} y={bounds.minY - 4000} width={width + 8000} height={height + 8000} fill="url(#collab-grid)" />
            {walls.length ? walls.map((wall) => (
              <line
                key={wall.id}
                x1={number(wall.x1) * scale}
                y1={number(wall.y1) * scale}
                x2={number(wall.x2) * scale}
                y2={number(wall.y2) * scale}
                stroke="#332f2b"
                strokeWidth={Math.max(5, number(wall.thickness, 0.2) * scale)}
                strokeLinecap="square"
              />
            )) : <rect x="4" y="4" width={width - 8} height={height - 8} fill="none" stroke="#332f2b" strokeWidth="8" />}
            {openings.map((opening) => <Opening key={opening.id} opening={opening} wall={walls.find((wall) => wall.id === (opening.wallId || opening.wall_id))} scale={scale} />)}

            {safeFurniture.map((item) => (
              <g key={item.key} transform={`rotate(${number(item.rotation)} ${item.x} ${item.y})`} pointerEvents="none">
                <rect
                  x={item.x - item.width / 2}
                  y={item.y - item.height / 2}
                  width={item.width}
                  height={item.height}
                  rx="6"
                  fill={item.color || FURNITURE_COLORS[item.type] || "#d8d0c0"}
                  fillOpacity="0.8"
                  stroke="#8f8574"
                  strokeWidth="1.5"
                  strokeDasharray={item.type === "dancefloor" ? "6 4" : undefined}
                />
                <text x={item.x} y={item.y + 4} textAnchor="middle" fontSize="12" fontWeight="600" fill="#4a4338">{item.label}</text>
              </g>
            ))}

            {safeTables.map((table) => {
              const dimmed = matches && !matches.has(table.key);
              const isSelected = table.key === selectedId;
              const chairs = chairPositions(table, table.seats);
              const names = new Map(table.seatedGuests.map((guest) => [number(guest.seat, -1), guest.name]));
              return (
                <g
                  key={table.key}
                  transform={`rotate(${number(table.rotation)} ${table.x} ${table.y})`}
                  opacity={dimmed ? 0.3 : 1}
                  onClick={() => selectTable(table.key)}
                  style={{ cursor: "pointer" }}
                  role="button"
                  aria-label={`${table.label}, ${table.seated} sur ${table.seats} places occupées`}
                >
                  {chairs.map((chair, index) => {
                    const occupied = table.seatedGuests.length
                      ? names.has(index)
                      : index < table.seated;
                    return (
                      <circle
                        key={index}
                        cx={table.x + chair.x}
                        cy={table.y + chair.y}
                        r="8"
                        fill={occupied ? "#4f6a4a" : "#f3efe6"}
                        stroke="#8f8574"
                        strokeWidth="1.2"
                      />
                    );
                  })}
                  {isRound(table) ? (
                    <circle cx={table.x} cy={table.y} r={Math.max(table.width, table.height) / 2} fill={statusColor(table.seated, table.seats)} stroke={isSelected ? "#241f1a" : "#c6a03a"} strokeWidth={isSelected ? 5 : 4} />
                  ) : (
                    <rect x={table.x - table.width / 2} y={table.y - table.height / 2} width={table.width} height={table.height} rx="8" fill={statusColor(table.seated, table.seats)} stroke={isSelected ? "#241f1a" : "#c6a03a"} strokeWidth={isSelected ? 5 : 4} />
                  )}
                  <text x={table.x} y={table.y + (table.seats ? -2 : 5)} textAnchor="middle" fontSize="14" fontWeight="700" fill="#fffaf1" pointerEvents="none">{table.label}</text>
                  {table.seats ? (
                    <text x={table.x} y={table.y + 14} textAnchor="middle" fontSize="11" fontWeight="600" fill="#fffaf1" pointerEvents="none">{table.seated}/{table.seats}</text>
                  ) : null}
                </g>
              );
            })}
          </svg>
        </div>

        <aside className="max-h-[520px] overflow-y-auto rounded-2xl border border-stone-200 bg-white/70 p-3" aria-label="Liste des tables">
          {selected ? (
            <div className="mb-3 rounded-xl border border-sage/30 bg-sage/10 p-3">
              <p className="font-serif text-lg text-charcoal">{selected.label}</p>
              <p className="text-xs text-charcoal-soft">{selected.seated} / {selected.seats} places occupées</p>
              {canSeeNames ? (
                selected.seatedGuests.length ? (
                  <ul className="mt-2 space-y-1 text-sm text-charcoal">
                    {selected.seatedGuests.map((guest) => <li key={guest.id}>{guest.name}</li>)}
                  </ul>
                ) : <p className="mt-2 text-xs italic text-charcoal-soft">Aucun invité placé.</p>
              ) : (
                <p className="mt-2 text-xs italic text-charcoal-soft">Les noms des invités ne sont pas partagés avec votre accès.</p>
              )}
            </div>
          ) : null}
          <ul className="space-y-1">
            {safeTables.map((table) => (
              <li key={table.key}>
                <button
                  type="button"
                  onClick={() => setSelectedId(table.key)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${table.key === selectedId ? "bg-sage/15 text-charcoal" : "text-charcoal-soft hover:bg-stone-100"} ${matches && !matches.has(table.key) ? "opacity-40" : ""}`}
                >
                  <span className="truncate font-medium">{table.label}</span>
                  <span className="flex shrink-0 items-center gap-1 text-xs"><Users className="h-3 w-3" aria-hidden="true" />{table.seated}/{table.seats}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 border-t border-stone-200 pt-3 text-[11px] text-charcoal-soft">
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#7f9a78" }} />Complète</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#d8b45a" }} />Partielle</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#c9c2b4" }} />Vide</span>
          </div>
        </aside>
      </div>
      <p className="mt-3 text-xs text-charcoal-soft">Les modifications du plan restent réservées aux mariés.</p>
    </section>
  );
}
